import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  IconButton,
  Collapse,
  Stack,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Public as GlobeIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import {
  getRestockSummary,
  getRestockDetails,
  getRestockRevenueImpact,
  exportRestockCSV,
  clearRestockCache,
  formatCurrency,
  formatNumber
} from '../../services/inventoryService';

// Alert status configuration
const ALERT_CONFIG = {
  out_of_stock: {
    label: 'Out of Stock',
    color: 'error',
    icon: ErrorIcon,
    bgColor: '#FFEBEE'
  },
  low_stock: {
    label: 'Low Stock',
    color: 'warning',
    icon: WarningIcon,
    bgColor: '#FFF3E0'
  },
  healthy: {
    label: 'Healthy',
    color: 'success',
    icon: CheckCircleIcon,
    bgColor: '#E8F5E9'
  },
  overstock: {
    label: 'Overstock',
    color: 'info',
    icon: InventoryIcon,
    bgColor: '#E3F2FD'
  }
};

// Country flag mapping
const COUNTRY_FLAGS = {
  'US': '🇺🇸', 'CA': '🇨🇦', 'MX': '🇲🇽', 'GB': '🇬🇧', 'UK': '🇬🇧',
  'DE': '🇩🇪', 'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸', 'NL': '🇳🇱',
  'SE': '🇸🇪', 'PL': '🇵🇱', 'BE': '🇧🇪', 'AU': '🇦🇺', 'SG': '🇸🇬', 'JP': '🇯🇵',
  'EU': '🇪🇺'
};

function AmazonRestockRecommendations() {
  // URL search params for shareable filters
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initialFilters = useMemo(() => ({
    brand: searchParams.get('brand') || '',
    alert: searchParams.get('alert') || '',
    country: searchParams.get('country') || '',
    search: searchParams.get('search') || '',
    tab: parseInt(searchParams.get('tab') || '0', 10),
    page: parseInt(searchParams.get('page') || '0', 10),
    sort: searchParams.get('sort') || 'revenue_impact',
    order: searchParams.get('order') || 'DESC'
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(initialFilters.tab);

  // Summary data
  const [summary, setSummary] = useState(null);

  // Details data with pagination
  const [details, setDetails] = useState([]);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [page, setPage] = useState(initialFilters.page);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState(initialFilters.sort);
  const [sortOrder, setSortOrder] = useState(initialFilters.order);

  // Filters
  const [brandFilter, setBrandFilter] = useState(initialFilters.brand);
  const [alertFilter, setAlertFilter] = useState(initialFilters.alert);
  const [countryFilter, setCountryFilter] = useState(initialFilters.country);
  const [searchTerm, setSearchTerm] = useState(initialFilters.search);

  // Popup state for breakdown dialogs
  const [breakdownPopup, setBreakdownPopup] = useState({
    open: false,
    type: null, // 'brand', 'geography', 'alert'
    data: null,
    title: ''
  });

  // Update URL when filters change
  const updateUrlParams = useCallback((updates) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== '' && value !== 0 && value !== '0') {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Revenue impact data
  const [revenueImpact, setRevenueImpact] = useState(null);

  // All brands/geographies for filter dropdowns (unfiltered)
  const [allBrands, setAllBrands] = useState([]);
  const [allGeographies, setAllGeographies] = useState([]);

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Load all brands/geographies once (unfiltered) for dropdown options
  const loadFilterOptions = useCallback(async () => {
    try {
      const res = await getRestockSummary({});
      if (res.success) {
        setAllBrands(res.data?.by_brand || []);
        setAllGeographies(res.data?.by_geography || []);
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, []);

  // Load summary data
  const loadSummary = useCallback(async () => {
    try {
      const params = {};
      if (brandFilter) params.brand_id = brandFilter;
      if (alertFilter) params.alert_type = alertFilter;

      const [summaryRes, revenueRes] = await Promise.all([
        getRestockSummary(params),
        getRestockRevenueImpact(params)
      ]);

      if (summaryRes.success) {
        setSummary(summaryRes.data);
      }
      if (revenueRes.success) {
        setRevenueImpact(revenueRes.data);
      }
    } catch (err) {
      console.error('Error loading summary:', err);
      setError('Failed to load restock summary');
    }
  }, [brandFilter, alertFilter]);

  // Load details data
  const loadDetails = useCallback(async () => {
    setDetailsLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (brandFilter) params.brand_id = brandFilter;
      if (alertFilter) params.alert_type = alertFilter;
      if (countryFilter) params.country = countryFilter;
      if (searchTerm) params.search = searchTerm;

      const res = await getRestockDetails(params);
      if (res.success) {
        setDetails(res.data.items || []);
        setDetailsTotal(res.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error loading details:', err);
      setError('Failed to load restock details');
    } finally {
      setDetailsLoading(false);
    }
  }, [brandFilter, alertFilter, countryFilter, searchTerm, page, rowsPerPage, sortBy, sortOrder]);

  // Load filter options once on mount (unfiltered brands/geographies for dropdowns)
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await loadSummary();
      setLoading(false);
    };
    loadData();
  }, [loadSummary]);

  // Load details when tab changes or filters/pagination change
  useEffect(() => {
    if (activeTab === 1) {
      loadDetails();
    }
  }, [activeTab, loadDetails]);

  // Handle sort
  const handleSort = (column) => {
    const newOrder = sortBy === column ? (sortOrder === 'ASC' ? 'DESC' : 'ASC') : 'DESC';
    if (sortBy === column) {
      setSortOrder(newOrder);
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
    updateUrlParams({ sort: column, order: newOrder });
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    updateUrlParams({ page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    updateUrlParams({ page: 0 });
  };

  // Handle refresh
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    // Clear cache and reload fresh data
    try {
      await clearRestockCache();
    } catch (err) {
      console.warn('Could not clear cache:', err);
    }
    await loadSummary();
    if (activeTab === 1) {
      await loadDetails();
    }
    setLoading(false);
  };

  // Handle CSV export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (brandFilter) params.brand_id = brandFilter;
      if (alertFilter) params.alert_type = alertFilter;
      if (countryFilter) params.country = countryFilter;
      if (searchTerm) params.search = searchTerm;

      await exportRestockCSV(params);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle search with debounce
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0);
    updateUrlParams({ search: value, page: 0 });
  };

  // Handle opening breakdown popup
  const openBreakdownPopup = (type, data, title) => {
    setBreakdownPopup({
      open: true,
      type,
      data,
      title
    });
  };

  // Handle closing breakdown popup
  const closeBreakdownPopup = () => {
    setBreakdownPopup({
      open: false,
      type: null,
      data: null,
      title: ''
    });
  };

  // Navigate to details with filter from popup
  const navigateToDetailsFromPopup = (filterType, filterValue) => {
    closeBreakdownPopup();
    if (filterType === 'brand') {
      setBrandFilter(filterValue);
      updateUrlParams({ brand: filterValue, tab: 1 });
    } else if (filterType === 'geography') {
      setCountryFilter(filterValue);
      updateUrlParams({ country: filterValue, tab: 1 });
    } else if (filterType === 'alert') {
      setAlertFilter(filterValue);
      updateUrlParams({ alert: filterValue, tab: 1 });
    }
    setActiveTab(1);
  };

  // Get alert chip
  const getAlertChip = (alert) => {
    const config = ALERT_CONFIG[alert] || ALERT_CONFIG.healthy;
    const Icon = config.icon;
    return (
      <Chip
        icon={<Icon sx={{ fontSize: 16 }} />}
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  // Render summary cards
  const renderSummaryCards = () => {
    if (!summary) return null;

    const { summary: stats, by_brand, by_geography, by_alert_status } = summary;

    return (
      <Grid container spacing={3}>
        {/* Main Stats Row */}
        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: '100%', bgcolor: '#fff', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => openBreakdownPopup('brand', by_brand, 'SKU Breakdown by Brand')}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InventoryIcon color="primary" />
                  <Typography color="text.secondary" variant="subtitle2">Total SKUs</Typography>
                </Stack>
                <Tooltip title="Click to see breakdown by brand">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>
              <Typography variant="h4" fontWeight="bold">
                {formatNumber(stats?.total_skus || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Across {stats?.total_brands || 0} brands
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: '100%', bgcolor: ALERT_CONFIG.out_of_stock.bgColor, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => openBreakdownPopup('alert', by_alert_status, 'Alert Status Breakdown')}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ErrorIcon color="error" />
                  <Typography color="error" variant="subtitle2">Out of Stock</Typography>
                </Stack>
                <Tooltip title="Click to see all alert statuses">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {formatNumber(stats?.out_of_stock_skus || 0)}
              </Typography>
              <Typography variant="body2" color="error.dark">
                SKUs need immediate attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: '100%', bgcolor: ALERT_CONFIG.low_stock.bgColor, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => openBreakdownPopup('alert', by_alert_status, 'Alert Status Breakdown')}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <WarningIcon color="warning" />
                  <Typography color="warning.dark" variant="subtitle2">Low Stock</Typography>
                </Stack>
                <Tooltip title="Click to see all alert statuses">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="warning.dark">
                {formatNumber(stats?.low_stock_skus || 0)}
              </Typography>
              <Typography variant="body2" color="warning.dark">
                SKUs running low
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: '100%', bgcolor: '#E3F2FD', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => openBreakdownPopup('geography', by_geography, 'Breakdown by Geography')}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ShippingIcon color="primary" />
                  <Typography color="primary" variant="subtitle2">Recommended to Ship</Typography>
                </Stack>
                <Tooltip title="Click to see breakdown by geography">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {formatNumber(stats?.total_recommended_qty || 0)}
              </Typography>
              <Typography variant="body2" color="primary.dark">
                Units to replenish FBA
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Rev. at Risk Row */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Monthly Revenue at Risk
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#F5F5F5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Potential Revenue (If Restocked)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(revenueImpact?.totals?.potential_revenue_if_restocked || stats?.total_potential_revenue || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      = Recommended Qty x Price
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFEBEE', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Estimated Monthly Loss (Stockouts)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {formatCurrency(revenueImpact?.totals?.estimated_monthly_loss || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on historical velocity
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#E8F5E9', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="success.dark" gutterBottom>
                      Current Monthly Revenue
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(revenueImpact?.totals?.monthly_revenue || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last 30 days sales
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Brand Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">
                  By Brand
                </Typography>
                <Button
                  size="small"
                  startIcon={<ExpandMoreIcon />}
                  onClick={() => openBreakdownPopup('brand', by_brand, 'Full Brand Breakdown')}
                >
                  View All
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Brand</TableCell>
                      <TableCell align="right">SKUs</TableCell>
                      <TableCell align="right">Out of Stock</TableCell>
                      <TableCell align="right">Recommended Qty</TableCell>
                      <TableCell align="right">Monthly Rev. at Risk</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(by_brand || []).slice(0, 10).map((brand) => (
                      <TableRow
                        key={brand.brand_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setBrandFilter(brand.brand_id);
                          setActiveTab(1);
                          updateUrlParams({ brand: brand.brand_id, tab: 1 });
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {brand.brand_name || `Brand ${brand.brand_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(brand.total_skus)}</TableCell>
                        <TableCell align="right">
                          {brand.out_of_stock > 0 ? (
                            <Chip label={brand.out_of_stock} color="error" size="small" />
                          ) : (
                            <Chip label="0" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatNumber(brand.recommended_qty)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={brand.revenue_impact > 0 ? 'success.main' : 'text.secondary'}
                            fontWeight={500}
                          >
                            {formatCurrency(brand.revenue_impact || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Geography Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">
                  <GlobeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  By Geography
                </Typography>
                <Button
                  size="small"
                  startIcon={<ExpandMoreIcon />}
                  onClick={() => openBreakdownPopup('geography', by_geography, 'Full Geography Breakdown')}
                >
                  View All
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Country</TableCell>
                      <TableCell align="right">SKUs</TableCell>
                      <TableCell align="right">Out of Stock</TableCell>
                      <TableCell align="right">Recommended Qty</TableCell>
                      <TableCell align="right">Monthly Rev. at Risk</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(by_geography || []).slice(0, 10).map((geo) => (
                      <TableRow
                        key={geo.country}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setCountryFilter(geo.country);
                          setActiveTab(1);
                          updateUrlParams({ country: geo.country, tab: 1 });
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography>{COUNTRY_FLAGS[geo.country] || ''}</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {geo.country}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{formatNumber(geo.total_skus)}</TableCell>
                        <TableCell align="right">
                          {geo.out_of_stock > 0 ? (
                            <Chip label={geo.out_of_stock} color="error" size="small" />
                          ) : (
                            <Chip label="0" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatNumber(geo.recommended_qty)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={geo.revenue_impact > 0 ? 'success.main' : 'text.secondary'}
                            fontWeight={500}
                          >
                            {formatCurrency(geo.revenue_impact || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render details table
  const renderDetailsTable = () => {
    return (
      <Box>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search SKU, ASIN, or product..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 250 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Brand</InputLabel>
              <Select
                value={brandFilter}
                onChange={(e) => {
                  setBrandFilter(e.target.value);
                  setPage(0);
                  updateUrlParams({ brand: e.target.value, page: 0 });
                }}
                label="Brand"
              >
                <MenuItem value="">All Brands</MenuItem>
                {(allBrands || []).map((brand) => (
                  <MenuItem key={brand.brand_id} value={brand.brand_id}>
                    {brand.brand_name || `Brand ${brand.brand_id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Alert Status</InputLabel>
              <Select
                value={alertFilter}
                onChange={(e) => {
                  setAlertFilter(e.target.value);
                  setPage(0);
                  updateUrlParams({ alert: e.target.value, page: 0 });
                }}
                label="Alert Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                <MenuItem value="low_stock">Low Stock</MenuItem>
                <MenuItem value="healthy">Healthy</MenuItem>
                <MenuItem value="overstock">Overstock</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Country</InputLabel>
              <Select
                value={countryFilter}
                onChange={(e) => {
                  setCountryFilter(e.target.value);
                  setPage(0);
                  updateUrlParams({ country: e.target.value, page: 0 });
                }}
                label="Country"
              >
                <MenuItem value="">All Countries</MenuItem>
                {(allGeographies || []).map((geo) => (
                  <MenuItem key={geo.country} value={geo.country}>
                    {COUNTRY_FLAGS[geo.country] || ''} {geo.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setBrandFilter('');
                setAlertFilter('');
                setCountryFilter('');
                setSearchTerm('');
                setPage(0);
                updateUrlParams({ brand: '', alert: '', country: '', search: '', page: 0 });
              }}
            >
              Clear Filters
            </Button>
          </Stack>
        </Paper>

        {/* Loading indicator */}
        {detailsLoading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ width: 50 }}>Image</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'brand_name'}
                    direction={sortBy === 'brand_name' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('brand_name')}
                  >
                    Brand
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'sku'}
                    direction={sortBy === 'sku' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('sku')}
                  >
                    SKU / ASIN
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <Tooltip title="Internal warehouse SKU (from product master)">
                    <span>Warehouse SKU</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Global Trade Item Number (UPC/EAN)">
                    <span>GTIN</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Alert</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'fba_available'}
                    direction={sortBy === 'fba_available' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('fba_available')}
                  >
                    FBA Available
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'fba_inbound'}
                    direction={sortBy === 'fba_inbound' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('fba_inbound')}
                  >
                    Incoming
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="FBA Reserved - inventory being processed for customer orders">
                    <span>Reserved</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'warehouse_soh'}
                    direction={sortBy === 'warehouse_soh' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('warehouse_soh')}
                  >
                    <Tooltip title="Stock on Hand in local warehouse (same geography)">
                      <span>Warehouse SOH</span>
                    </Tooltip>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'recommended_qty'}
                    direction={sortBy === 'recommended_qty' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('recommended_qty')}
                  >
                    Recommended Qty
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'price'}
                    direction={sortBy === 'price' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('price')}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'revenue_impact'}
                    direction={sortBy === 'revenue_impact' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('revenue_impact')}
                  >
                    Monthly Rev. at Risk
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'days_of_supply'}
                    direction={sortBy === 'days_of_supply' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('days_of_supply')}
                  >
                    Days of Supply
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'units_sold_30d'}
                    direction={sortBy === 'units_sold_30d' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('units_sold_30d')}
                  >
                    30d Sales
                  </TableSortLabel>
                </TableCell>
                <TableCell>Ship By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.map((row, index) => (
                <TableRow
                  key={`${row.brand_id}-${row.sku}-${row.country}-${index}`}
                  hover
                  sx={{
                    bgcolor: row.alert === 'out_of_stock' ? '#FFEBEE' :
                             row.alert === 'low_stock' ? '#FFF3E0' : 'inherit'
                  }}
                >
                  {/* Product Image */}
                  <TableCell sx={{ p: 0.5 }}>
                    {row.thumbnail_url ? (
                      <Tooltip title={row.product_name || 'Product image'}>
                        <Box
                          component="img"
                          src={row.thumbnail_url}
                          alt=""
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: '1px solid #eee'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </Tooltip>
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <InventoryIcon sx={{ fontSize: 20, color: '#ccc' }} />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.brand_name || `Brand ${row.brand_id}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.sku}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.asin}
                    </Typography>
                  </TableCell>
                  {/* Warehouse SKU */}
                  <TableCell>
                    <Typography variant="body2" color={row.warehouse_sku ? 'text.primary' : 'text.disabled'}>
                      {row.warehouse_sku || '-'}
                    </Typography>
                  </TableCell>
                  {/* GTIN */}
                  <TableCell>
                    <Typography variant="caption" color={row.gtin ? 'text.secondary' : 'text.disabled'}>
                      {row.gtin || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200, maxWidth: 300 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4
                      }}
                    >
                      {row.product_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography>{COUNTRY_FLAGS[row.country] || ''}</Typography>
                      <Typography variant="body2">{row.country}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {getAlertChip(row.alert)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color={
                        (row.fba_available || 0) === 0 ? 'error.main' :
                        (row.fba_available || 0) < 10 ? 'warning.main' : 'success.main'
                      }
                    >
                      {formatNumber(row.fba_available || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={(row.fba_inbound || 0) > 0 ? 'info.main' : 'text.secondary'}
                    >
                      {formatNumber(row.fba_inbound || 0)}
                    </Typography>
                  </TableCell>
                  {/* FBA Reserved */}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={(row.fba_reserved || 0) > 0 ? 'warning.main' : 'text.secondary'}
                    >
                      {formatNumber(row.fba_reserved || 0)}
                    </Typography>
                  </TableCell>
                  {/* Warehouse SOH */}
                  <TableCell align="right">
                    <Tooltip title={row.warehouse_soh > 0 ? `Available in local ${row.country} warehouse` : 'No local warehouse stock'}>
                      <Typography
                        variant="body2"
                        fontWeight={row.warehouse_soh > 0 ? 500 : 400}
                        color={
                          row.warehouse_soh > 0
                            ? (row.warehouse_soh >= (row.recommended_replenishment_qty || 0) ? 'success.main' : 'warning.main')
                            : 'text.disabled'
                        }
                      >
                        {row.warehouse_soh > 0 ? formatNumber(row.warehouse_soh) : '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500} color="primary">
                      {formatNumber(row.recommended_replenishment_qty || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(row.price || 0, row.currency_code || 'USD')}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color={row.revenue_impact > 0 ? 'success.main' : 'text.secondary'}
                    >
                      {formatCurrency(row.revenue_impact || 0, row.currency_code || 'USD')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={
                        (row.days_of_supply_at_amazon || 0) <= 7 ? 'error.main' :
                        (row.days_of_supply_at_amazon || 0) <= 14 ? 'warning.main' : 'text.primary'
                      }
                    >
                      {row.days_of_supply_at_amazon ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.units_sold_last_30_days || 0)}
                  </TableCell>
                  <TableCell>
                    {row.recommended_ship_date ? (
                      <Typography variant="body2">
                        {new Date(row.recommended_ship_date).toLocaleDateString()}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {details.length === 0 && !detailsLoading && (
                <TableRow>
                  <TableCell colSpan={17} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No restock recommendations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={detailsTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    );
  };

  // Main render
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Amazon FBA Restock Recommendations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cross-brand view of FBA inventory replenishment needs with revenue impact analysis
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={loading || exporting}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Global Brand Filter - applies to both Summary and Details */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Filter:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              value={brandFilter}
              onChange={(e) => {
                setBrandFilter(e.target.value);
                setPage(0);
                updateUrlParams({ brand: e.target.value, page: 0 });
              }}
              label="Brand"
            >
              <MenuItem value="">All Brands</MenuItem>
              {(allBrands || []).map((brand) => (
                <MenuItem key={brand.brand_id} value={brand.brand_id}>
                  {brand.brand_name || `Brand ${brand.brand_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {brandFilter && (
            <Button
              size="small"
              onClick={() => {
                setBrandFilter('');
                setPage(0);
                updateUrlParams({ brand: '', page: 0 });
              }}
            >
              Clear Filter
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => { setActiveTab(v); updateUrlParams({ tab: v }); }}>
          <Tab label="Summary Dashboard" />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Detailed View</span>
                {(summary?.summary?.out_of_stock_skus || 0) > 0 && (
                  <Chip
                    label={summary.summary.out_of_stock_skus}
                    color="error"
                    size="small"
                  />
                )}
              </Stack>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderSummaryCards()}
      {activeTab === 1 && renderDetailsTable()}

      {/* Breakdown Popup Dialog */}
      <Dialog
        open={breakdownPopup.open}
        onClose={closeBreakdownPopup}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{breakdownPopup.title}</Typography>
          <IconButton onClick={closeBreakdownPopup} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {breakdownPopup.type === 'brand' && breakdownPopup.data && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Click a row to view detailed SKUs for that brand
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Brand</TableCell>
                      <TableCell align="right">Total SKUs</TableCell>
                      <TableCell align="right">Out of Stock</TableCell>
                      <TableCell align="right">Low Stock</TableCell>
                      <TableCell align="right">Recommended Qty</TableCell>
                      <TableCell align="right">Revenue at Risk</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(breakdownPopup.data || []).map((brand) => (
                      <TableRow
                        key={brand.brand_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigateToDetailsFromPopup('brand', brand.brand_id)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {brand.brand_name || `Brand ${brand.brand_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(brand.total_skus)}</TableCell>
                        <TableCell align="right">
                          {brand.out_of_stock > 0 ? (
                            <Chip label={brand.out_of_stock} color="error" size="small" />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {brand.low_stock > 0 ? (
                            <Chip label={brand.low_stock} color="warning" size="small" />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">{formatNumber(brand.recommended_qty)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={brand.revenue_impact > 0 ? 'error.main' : 'text.secondary'}
                            fontWeight={500}
                          >
                            {formatCurrency(brand.revenue_impact || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {breakdownPopup.type === 'geography' && breakdownPopup.data && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Click a row to view detailed SKUs for that region
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Region</TableCell>
                      <TableCell align="right">Total SKUs</TableCell>
                      <TableCell align="right">Out of Stock</TableCell>
                      <TableCell align="right">Low Stock</TableCell>
                      <TableCell align="right">Recommended Qty</TableCell>
                      <TableCell align="right">Revenue at Risk</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(breakdownPopup.data || []).map((geo) => (
                      <TableRow
                        key={geo.country}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigateToDetailsFromPopup('geography', geo.country)}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography>{COUNTRY_FLAGS[geo.country] || ''}</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {geo.country}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{formatNumber(geo.total_skus)}</TableCell>
                        <TableCell align="right">
                          {geo.out_of_stock > 0 ? (
                            <Chip label={geo.out_of_stock} color="error" size="small" />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {geo.low_stock > 0 ? (
                            <Chip label={geo.low_stock} color="warning" size="small" />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">{formatNumber(geo.recommended_qty)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={geo.revenue_impact > 0 ? 'error.main' : 'text.secondary'}
                            fontWeight={500}
                          >
                            {formatCurrency(geo.revenue_impact || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {breakdownPopup.type === 'alert' && breakdownPopup.data && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Click a status to view all SKUs with that status
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">SKU Count</TableCell>
                      <TableCell align="right">Recommended Qty</TableCell>
                      <TableCell align="right">Revenue Impact</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(breakdownPopup.data || []).map((alert) => {
                      const statusKey = alert.alert_status?.toLowerCase().replace(/ /g, '_');
                      const config = ALERT_CONFIG[statusKey] || ALERT_CONFIG.healthy;
                      const Icon = config.icon;
                      return (
                        <TableRow
                          key={alert.alert_status}
                          hover
                          sx={{ cursor: 'pointer', bgcolor: config.bgColor }}
                          onClick={() => navigateToDetailsFromPopup('alert', statusKey)}
                        >
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Icon color={config.color} fontSize="small" />
                              <Typography variant="body2" fontWeight={500}>
                                {alert.alert_status}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatNumber(alert.count)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatNumber(alert.recommended_qty)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={alert.revenue_impact > 0 ? 'error.main' : 'text.secondary'}
                              fontWeight={500}
                            >
                              {formatCurrency(alert.revenue_impact || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button size="small" variant="outlined" color={config.color}>
                              View SKUs
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBreakdownPopup}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AmazonRestockRecommendations;
